import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Lock, Check, Eye, EyeOff } from 'lucide-react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../lib/auth'
import { useToast } from '../components/ui/Toast'
import Button from '../components/ui/Button'

function strengthScore(pw) {
  let score = 0
  if (pw.length >= 8) score++
  if (pw.length >= 12) score++
  if (/[a-z]/.test(pw) && /[A-Z]/.test(pw)) score++
  if (/\d/.test(pw)) score++
  if (/[^a-zA-Z0-9]/.test(pw)) score++
  return score
}

const STRENGTH_LABELS = ['', 'Weak', 'Fair', 'Good', 'Strong', 'Very Strong']
const STRENGTH_COLORS = ['', '#ba1a1a', '#8b4823', '#8b4823', '#006565', '#004b4b']

export default function SetPasswordPage() {
  const navigate = useNavigate()
  const { clearPasswordSetup } = useAuth()
  const toast = useToast()
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [showPw, setShowPw] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  const score = strengthScore(password)
  const mismatch = confirm.length > 0 && password !== confirm
  const tooShort = password.length > 0 && password.length < 8
  const canSubmit = password.length >= 8 && password === confirm && !saving

  async function handleSubmit(e) {
    e.preventDefault()
    if (!canSubmit) return
    setSaving(true)
    setError('')

    const { error: err } = await supabase.auth.updateUser({ password })

    if (err) {
      setSaving(false)
      setError(err.message)
      return
    }

    // BIG-60 — clear the must_change_password flag AFTER the password
    // update succeeds. Order matters: if the flag were cleared first and
    // the password update failed, the user would land back in the app
    // with the admin-typed temp password still active.
    // Best-effort: a failure here doesn't block sign-in (the flag will
    // simply trip the redirect on next login, which is recoverable).
    try { await supabase.rpc('clear_must_change_password') } catch { /* noop */ }

    setSaving(false)
    clearPasswordSetup()
    toast('Password set successfully')
    navigate('/', { replace: true })
  }

  return (
    <div className="min-h-screen bg-surface flex items-center justify-center px-4">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <div className="w-14 h-14 rounded-full mx-auto mb-4 flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #004b4b 0%, #006565 100%)' }}>
            <Lock size={24} className="text-white" />
          </div>
          <h1 className="text-2xl font-bold text-on-surface tracking-tight">Set Your Password</h1>
          <p className="text-sm text-on-surface-variant mt-2">Create a password to secure your account</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-xs font-semibold tracking-[0.05em] uppercase text-on-surface-variant mb-1.5">
              New Password
            </label>
            <div className="relative">
              <input
                type={showPw ? 'text' : 'password'}
                value={password}
                onChange={e => setPassword(e.target.value)}
                className="w-full px-4 py-3 pr-12 rounded-xl bg-surface-container-lowest text-sm text-on-surface outline-none ghost-border focus:ring-2 focus:ring-primary"
                placeholder="Min 8 characters"
                autoFocus
                autoComplete="new-password"
              />
              <button
                type="button"
                onClick={() => setShowPw(!showPw)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-on-surface-variant p-1"
                tabIndex={-1}
              >
                {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
            {password.length > 0 && (
              <div className="mt-2">
                <div className="flex gap-1 mb-1">
                  {[1, 2, 3, 4, 5].map(i => (
                    <div
                      key={i}
                      className="h-1 flex-1 rounded-full"
                      style={{ background: i <= score ? STRENGTH_COLORS[score] : '#ece7de' }}
                    />
                  ))}
                </div>
                <p className="text-[0.6875rem]" style={{ color: STRENGTH_COLORS[score] }}>
                  {STRENGTH_LABELS[score]}
                </p>
              </div>
            )}
            {tooShort && (
              <p className="text-xs text-error mt-1">Must be at least 8 characters</p>
            )}
          </div>

          <div>
            <label className="block text-xs font-semibold tracking-[0.05em] uppercase text-on-surface-variant mb-1.5">
              Confirm Password
            </label>
            <input
              type="password"
              value={confirm}
              onChange={e => setConfirm(e.target.value)}
              className="w-full px-4 py-3 rounded-xl bg-surface-container-lowest text-sm text-on-surface outline-none ghost-border focus:ring-2 focus:ring-primary"
              placeholder="Type it again"
              autoComplete="new-password"
            />
            {mismatch && (
              <p className="text-xs text-error mt-1">Passwords don't match</p>
            )}
            {confirm.length > 0 && !mismatch && (
              <p className="text-xs text-primary mt-1 flex items-center gap-1"><Check size={12} /> Matches</p>
            )}
          </div>

          {error && (
            <p className="text-sm text-error bg-error-container px-3 py-2 rounded-lg">{error}</p>
          )}

          <Button type="submit" className="w-full" disabled={!canSubmit}>
            {saving ? 'Setting Password…' : 'Set Password'}
          </Button>
        </form>
      </div>
    </div>
  )
}
